/**
 * Rewrite LumiBot trades.html Plotly buy/sell markers using the same
 * completed-fill replay as the indicator chart.
 *
 * WHY: trades.html is generated once at backtest end with generic buy/sell
 * traces. Crypto futures fills still mean open long, close short, reverse,
 * and so on. Deriving that at view time updates already-finished backtests
 * without changing broker or LumiBot runtime behavior.
 */
import Papa from 'papaparse';
import {
  futuresPositionActionLabel,
  normalizeFuturesTrades,
  type BacktestTrade,
  type FuturesPositionAction,
} from './futuresTradeSemantics';

export type FuturesPlotAction = {
  timeMs: number;
  symbol: string;
  side: string;
  qty: number;
  positionAction: FuturesPositionAction;
  label: string;
  hoverDetail: string;
};

export type FuturesPlotStyle = { color: string; symbol: string };

type PlotlyTraceLike = {
  name?: string;
  mode?: string;
  x?: unknown[];
  y?: unknown[];
  text?: unknown[];
  marker?: {
    symbol?: string;
    color?: string;
    size?: number;
  };
  hovertemplate?: string;
  [key: string]: unknown;
};

export const FUTURES_PLOTLY_MARKER_STYLES: Record<string, FuturesPlotStyle> = {
  open_long: { color: '#22C55E', symbol: 'triangle-up' },
  close_long: { color: '#F59E0B', symbol: 'square' },
  open_short: { color: '#EF4444', symbol: 'triangle-down' },
  close_short: { color: '#38BDF8', symbol: 'square' },
  reverse_to_long: { color: '#A78BFA', symbol: 'triangle-up' },
  reverse_to_short: { color: '#F472B6', symbol: 'triangle-down' },
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export function parseBacktestTradesCsv(csv: string): BacktestTrade[] {
  if (!csv || typeof csv !== 'string') return [];

  const rows = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
  }).data;

  return rows.map((row) => ({
    time: row.time,
    side: row.side,
    price: toNumber(row.price),
    qty: toNumber(row.filled_quantity),
    status: row.status || row.order_status || '-',
    symbol: row.symbol || row['asset.symbol'] || '',
    assetType: row['asset.asset_type'] || 'stock',
  }));
}

export function toFuturesPlotActions(trades: BacktestTrade[] = []): FuturesPlotAction[] {
  return normalizeFuturesTrades(trades).map((trade) => {
    const label = futuresPositionActionLabel(trade.positionAction);
    const before = trade.positionBefore === 0
      ? 'flat'
      : `${trade.positionBefore > 0 ? 'long' : 'short'} ${Math.abs(trade.positionBefore)}`;
    const after = trade.positionAfter === 0
      ? 'flat'
      : `${trade.positionAfter > 0 ? 'long' : 'short'} ${Math.abs(trade.positionAfter)}`;

    return {
      timeMs: new Date(String(trade.time)).getTime(),
      symbol: trade.symbol,
      side: trade.side,
      qty: trade.qty,
      positionAction: trade.positionAction,
      label,
      hoverDetail: `${label}: ${trade.side.replace(/_/g, ' ')} ${trade.qty} ${trade.symbol} at ${trade.price}. Position ${before} to ${after}.`,
    };
  });
}

/**
 * Self-contained so it can be injected into the trades.html blob page.
 * Do not close over imports; pass actions and styles in.
 */
export function splitPlotlyTradesByFuturesAction(
  data: PlotlyTraceLike[] | unknown,
  actions?: FuturesPlotAction[] | unknown,
  styles?: Record<string, Partial<FuturesPlotStyle>> | null,
): PlotlyTraceLike[] | unknown {
  if (!Array.isArray(data) || !Array.isArray(actions) || actions.length === 0) {
    return data;
  }

  const stylesMap = styles || {};
  const buySides: Record<string, true> = {
    buy: true,
    buy_to_open: true,
    buy_to_cover: true,
    buy_to_close: true,
  };

  function traceKind(name: unknown): 'buy' | 'sell' | null {
    const normalized = String(name || '').toLowerCase();
    if (normalized === 'buy') return 'buy';
    if (normalized === 'sell') return 'sell';
    return null;
  }

  function isBuyAction(side: unknown): boolean {
    return Boolean(buySides[String(side || '').toLowerCase()]);
  }

  const unused = (actions as FuturesPlotAction[]).map(function (action) {
    return { action: action, used: false };
  });

  function takeAction(kind: 'buy' | 'sell', text: unknown): FuturesPlotAction | null {
    const textUpper = String(text || '').toUpperCase();
    let fallback: { action: FuturesPlotAction; used: boolean } | null = null;
    for (let i = 0; i < unused.length; i += 1) {
      if (unused[i].used) continue;
      const action = unused[i].action;
      const actionIsBuy = isBuyAction(action.side);
      if (kind === 'buy' && !actionIsBuy) continue;
      if (kind === 'sell' && actionIsBuy) continue;
      if (!fallback) fallback = unused[i];
      if (action.symbol && textUpper.indexOf(String(action.symbol).toUpperCase()) !== -1) {
        unused[i].used = true;
        return action;
      }
    }
    if (fallback) {
      fallback.used = true;
      return fallback.action;
    }
    return null;
  }

  type GroupedTrace = PlotlyTraceLike & { x: unknown[]; y: unknown[]; text: unknown[] };
  const grouped: Record<string, GroupedTrace> = {};
  const leftovers: Record<'buy' | 'sell', {
    x: unknown[];
    y: unknown[];
    text: unknown[];
    source: PlotlyTraceLike | null;
  }> = {
    buy: { x: [], y: [], text: [], source: null },
    sell: { x: [], y: [], text: [], source: null },
  };
  const result: PlotlyTraceLike[] = [];
  const traces = data as PlotlyTraceLike[];

  for (let t = 0; t < traces.length; t += 1) {
    const trace = traces[t];
    const kind = traceKind(trace && trace.name);
    if (!kind) {
      result.push(trace);
      continue;
    }

    const xs = Array.isArray(trace.x) ? trace.x : [];
    const ys = Array.isArray(trace.y) ? trace.y : [];
    const texts = Array.isArray(trace.text) ? trace.text : [];
    leftovers[kind].source = trace;

    for (let p = 0; p < xs.length; p += 1) {
      const action = takeAction(kind, texts[p]);
      if (!action) {
        leftovers[kind].x.push(xs[p]);
        leftovers[kind].y.push(ys[p]);
        leftovers[kind].text.push(texts[p]);
        continue;
      }

      const key = action.positionAction;
      if (!grouped[key]) {
        const style = stylesMap[key] || {};
        grouped[key] = {
          name: action.label,
          mode: 'markers',
          x: [],
          y: [],
          text: [],
          marker: {
            symbol: style.symbol || (kind === 'buy' ? 'triangle-up' : 'triangle-down'),
            color: style.color || (kind === 'buy' ? 'green' : 'red'),
            size: (trace.marker && trace.marker.size) || 15,
          },
          hovertemplate: action.label + '<br>%{text}<br>%{x|%b %d %Y %I:%M:%S %p}<extra></extra>',
        };
      }
      grouped[key].x.push(xs[p]);
      grouped[key].y.push(ys[p]);
      grouped[key].text.push(
        (action.hoverDetail ? action.hoverDetail + '<br>' : '') + String(texts[p] || '')
      );
    }
  }

  const actionOrder = [
    'open_long',
    'close_long',
    'open_short',
    'close_short',
    'reverse_to_long',
    'reverse_to_short',
  ];
  for (let a = 0; a < actionOrder.length; a += 1) {
    if (grouped[actionOrder[a]]) result.push(grouped[actionOrder[a]]);
  }

  (['buy', 'sell'] as const).forEach(function (kind) {
    if (leftovers[kind].x.length && leftovers[kind].source) {
      result.push(Object.assign({}, leftovers[kind].source, {
        x: leftovers[kind].x,
        y: leftovers[kind].y,
        text: leftovers[kind].text,
      }));
    }
  });

  return result;
}

export function enhanceTradesHtmlWithFuturesMarkers(
  html: string,
  trades: BacktestTrade[] = [],
): string {
  if (!html || typeof html !== 'string' || html.indexOf('Plotly.newPlot') === -1) {
    return html;
  }

  const actions = toFuturesPlotActions(trades);
  if (actions.length === 0) return html;

  const prelude = [
    // Assign to a stable name. Production minify can rename the function in
    // toString() (for example `function n(...)`), and the blob page would then
    // call an undefined identifier.
    'var splitPlotlyTradesByFuturesAction = ' + splitPlotlyTradesByFuturesAction.toString() + ';',
    'var __botspotFuturesPlotActions = ' + JSON.stringify(actions) + ';',
    'var __botspotFuturesPlotStyles = ' + JSON.stringify(FUTURES_PLOTLY_MARKER_STYLES) + ';',
    'if (typeof Plotly !== "undefined" && Plotly.newPlot && !Plotly.newPlot.__botspotFuturesWrapped) {',
    '  var __botspotOriginalNewPlot = Plotly.newPlot.bind(Plotly);',
    '  Plotly.newPlot = function(gd, data, layout, config) {',
    '    return __botspotOriginalNewPlot(gd, splitPlotlyTradesByFuturesAction(data, __botspotFuturesPlotActions, __botspotFuturesPlotStyles), layout, config);',
    '  };',
    '  Plotly.newPlot.__botspotFuturesWrapped = true;',
    '}',
  ].join('\n');

  return html.replace(/Plotly\.newPlot\s*\(/, prelude + '\nPlotly.newPlot(');
}
